import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { getRun, getVersionIdentity, listMyAssignedRuns } from "../lib/api";
import { clientError } from "../lib/clientMessage";
import { currentAssessment } from "../lib/respondent";
import { SNAPSHOT_SLUG } from "../lib/snapshot";
import { RespondAssessment } from "./RespondAssessment";
import { RespondReport } from "./RespondReport";
import { RespondSnapshot } from "./RespondSnapshot";
import { SnapshotReport } from "./SnapshotReport";

// One switch, by the run's instrument (SPECIFICATION_TRANSFORMATION_SURVEYS
// 011126 V1.0.md, slice 1): a Transformation Snapshot run gets its own
// questionnaire and report; every other run gets the screens it had before,
// unchanged. The PMO screens are not edited for the Snapshot.

type Pick = { kind: "loading" } | { kind: "error"; message: string } | { kind: "ready"; runId: string | null; slug: string };

function usePick(find: () => Promise<{ runId: string; versionId: string } | null>, deps: unknown[]): Pick {
  const [pick, setPick] = useState<Pick>({ kind: "loading" });
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const found = await find();
        const slug = found ? (await getVersionIdentity(found.versionId)).slug : "";
        if (live) setPick({ kind: "ready", runId: found?.runId ?? null, slug });
      } catch (e) {
        if (live) setPick({ kind: "error", message: clientError("load", e).message });
      }
    })();
    return () => {
      live = false;
    };
  }, deps);
  return pick;
}

async function myCurrent() {
  const mine = currentAssessment(await listMyAssignedRuns());
  return mine ? { runId: mine.run_id, versionId: mine.instrument_version_id } : null;
}

/** The respondent's /respond: the Snapshot questionnaire, or the PMO one. */
export function RespondHome() {
  const pick = usePick(myCurrent, []);
  if (pick.kind === "loading") return <p>Loading your assessment…</p>;
  if (pick.kind === "error") return <div className="error-banner" role="alert">{pick.message}</div>;
  if (pick.slug === SNAPSHOT_SLUG && pick.runId) return <RespondSnapshot runId={pick.runId} />;
  return <RespondAssessment />;
}

/** The respondent's /respond/report. */
export function RespondReportHome() {
  const pick = usePick(myCurrent, []);
  if (pick.kind === "loading") return <p>Preparing your report…</p>;
  if (pick.kind === "error") return <div className="error-banner" role="alert">{pick.message}</div>;
  if (pick.slug === SNAPSHOT_SLUG && pick.runId) return <SnapshotReport runId={pick.runId} />;
  return <RespondReport />;
}

/** The Owner's report and dashboard routes: a Snapshot run shows its short report there. */
export function RunReportByInstrument({ children }: { children: ReactNode }) {
  const { runId = "" } = useParams();
  const pick = usePick(async () => {
    const run = await getRun(runId);
    return run ? { runId: run.id, versionId: run.instrument_version_id } : null;
  }, [runId]);
  if (pick.kind === "loading") return <p>Loading…</p>;
  if (pick.kind === "error") return <div className="error-banner" role="alert">{pick.message}</div>;
  if (pick.slug === SNAPSHOT_SLUG && pick.runId) return <SnapshotReport runId={pick.runId} />;
  return <>{children}</>;
}
